import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { post } from '../services/apiClient';
import '../css/SupportPage.css'

function SupportPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [responseMessage, setResponseMessage] = useState('');
    const [token, setToken] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successCase, setSuccessCase] = useState(null);
    const widgetRef = useRef(null);

    // Load Cloudflare Turnstile (optional if site key provided)
    //
    useEffect(() => {
        const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
        if (!siteKey) return; // skip if not configured
        if (document.querySelector('script[data-turnstile]')) return; // already loaded
        const s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.setAttribute('data-turnstile', '1');
        s.onload = () => {
            if (window.turnstile && widgetRef.current) {
                window.turnstile.render(widgetRef.current, {
                    sitekey: siteKey,
                    callback: (t) => setToken(t),
                });
            }
        };
        document.body.appendChild(s);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setResponseMessage('Sending...');

        // simple client-side validation
        const cleanName = name.trim().slice(0, 100);
        const cleanEmail = email.trim();
        const cleanMessage = message.trim().slice(0, 2000);
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail);
        if (!emailOk || !cleanMessage) {
            setResponseMessage('Please enter a valid email and message.');
            setIsSubmitting(false);
            return;
        }

        try {
            console.log('Sending support email...'); // Debug log
            const response = await post('/send-support-email', { 
                name: cleanName, 
                email: cleanEmail, 
                message: cleanMessage, 
                turnstileToken: token 
            });
            
            console.log('Response received:', response); // Debug log
            
            // Check multiple possible response formats
            const caseNumber = response.caseNumber || response.case_number || response.caseNum;
            
            if (caseNumber) {
                // Success - show the case number
                setSuccessCase(caseNumber);
                setResponseMessage('');
                
                // Clear form
                setName('');
                setEmail('');
                setMessage('');
                setToken('');
                
                // Reset Turnstile if present
                if (window.turnstile) {
                    window.turnstile.reset();
                }
            } else {
                console.error('No case number in response:', response);
                setResponseMessage('Email sent but no case number received. Please check your email.');
                setSuccessCase(null);
            }
        } catch (err) {
            console.error('Error sending support email:', err); // Debug log
            setResponseMessage('Failed to send email. Please try again or email us directly at support@familyring.io.');
            setSuccessCase(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    // If successfully submitted, show success message
    if (successCase) {
        return (
            <section className="py-5">
                <div className="container" style={{maxWidth: 720}}>
                    <div className="text-center mb-4">
                        <h2 className="section-title">Thank You!</h2>
                    </div>
                    <div className="feature-card text-center">
                        <div className="success-icon mb-3">
                            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="32" cy="32" r="32" fill="#5D2FFF" opacity="0.1"/>
                                <path d="M22 32L28 38L42 24" stroke="#5D2FFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <h3 className="mb-3">Your support request has been received</h3>
                        <div className="case-number-box mb-4">
                            <p className="mb-1 text-muted">Your case number is:</p>
                            <h4 className="case-number">{successCase}</h4>
                        </div>
                        <p className="text-muted mb-4">
                            We've sent a confirmation email to your address. Our support team will respond within 24-48 hours.
                        </p>
                        <div className="d-flex flex-column align-items-center gap-2">
                            <button 
                                onClick={() => setSuccessCase(null)} 
                                className="btn btn-primary"
                                style={{backgroundColor: '#8360FF', borderColor: '#8360FF'}}
                            >
                                Submit Another Request
                            </button>
                            <Link to="/" className="text-decoration-none" style={{color: '#5D2FFF'}}>
                                ← Return to Home Page
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="py-5">
            <div className="container" style={{maxWidth: 720}}>
                <div className="text-center mb-4">
                    <h2 className="section-title">Contact Support</h2>
                    <p className="text-muted">We'll get back to you shortly.</p>
                </div>
                <div className="feature-card">
                    <form onSubmit={handleSubmit} className="support-form">
                        <div className="form-group mb-3">
                            <label className="form-label">Your name (optional)</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="form-control"
                                maxLength={100}
                                placeholder="Jane Doe"
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="form-group mb-3">
                            <label className="form-label">Your email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="form-control"
                                placeholder="you@example.com"
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="form-group mb-3">
                            <label className="form-label">Message</label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                                className="form-control"
                                rows={5}
                                placeholder="How can we help?"
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="form-group mb-3">
                            {/* CAPTCHA placeholder renders here if a site key is set */}
                            <div ref={widgetRef}></div>
                        </div>
                        <button 
                            type="submit" 
                            className="btn btn-brand" 
                            disabled={isSubmitting}
                        >
                            {isSubmitting && (
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                    style={{ color: 'var(--brand-primary)' }}
                                />
                            )}
                            {isSubmitting 
                                ? <span style={{ color: 'var(--brand-primary)' }}>Sending...</span>
                                : 'Send message'}
                        </button>
                        {!isSubmitting && responseMessage && (
                            <p className="mt-3 mb-0 text-danger" aria-live="polite">{responseMessage}</p>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
}

export default SupportPage;
